import { Component, Inject, ViewChild, ElementRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { EclaimsAdobeAnalytics } from '@app/shared/utils/adobe/eclaims-adobe-analytics';
import { EclaimsAdobeTags } from '@app/shared/utils/adobe/eclaims-adobe-tags';
import { buildSafeHashRouteUrl } from '@app/shared/utils/routing-url.util';

@Component({
  selector: 'app-terms-of-service',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, ButtonComponent, TranslateModule],
  templateUrl: './terms-of-service.component.html',
  styleUrl: './terms-of-service.component.scss',
})
export class TermsOfServiceComponent implements OnInit {
  @ViewChild('dialogContent', { static: false }) dialogContent!: ElementRef;
  router = inject(Router);
  currentStep = 0;
  totalSteps = 3;
  isScrolledToEnd = false;

  // Translation data properties
  step2ListItems: string[] = [];
  step3Conditions: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<TermsOfServiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService
  ) {}



  ngOnInit() {
    // Initialize translation service only for this component
    this.translateService.use('ja');
    
    // Load translation data for complex structures
    this.loadTranslationData();

    EclaimsAdobeAnalytics.trackPageLoad(EclaimsAdobeTags.GUIDANCE.CONDITION_TERMS_PAGE_NAME);
  }

  private loadTranslationData() {
    // Wait for translations to load before getting data
    this.translateService.onLangChange.subscribe(() => {
      this.loadTranslationArrays();
    });
    
    // Also try to load immediately if translations are already available
    this.loadTranslationArrays();
  }

  private loadTranslationArrays() {
    // Load Step 2 list items
    this.translateService.get('TERMS_OF_SERVICE.STEP_2.CONTENT.ITEM_1.LIST_ITEMS').subscribe(
      (items: string[]) => {
        if (Array.isArray(items)) {
          this.step2ListItems = items;
        }
      }
    );

    // Load Step 3 conditions
    this.translateService.get('TERMS_OF_SERVICE.STEP_3.CONDITIONS').subscribe(
      (conditions: any[]) => {
        if (Array.isArray(conditions)) {
          this.step3Conditions = conditions;
        }
      }
    );
  }

  nextStep(): void {
    let nextPageName = '';

    if(this.currentStep == 0){
      EclaimsAdobeAnalytics.trackLinkClick({
        label: EclaimsAdobeTags.GUIDANCE.CONDITION_AGREE_LINK_NAME
      });
      nextPageName = EclaimsAdobeTags.GUIDANCE.PRIVACY_POLICY_PAGE_NAME;
    }else if(this.currentStep == 1){
      EclaimsAdobeAnalytics.trackLinkClick({
        label: EclaimsAdobeTags.GUIDANCE.PRIVACY_POLICY_AGREE_LINK_NAME
      });
      nextPageName = EclaimsAdobeTags.GUIDANCE.USAGE_COND_PAGE_NAME;
    }
    
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
      if (nextPageName) {
        EclaimsAdobeAnalytics.trackPageLoad(nextPageName);
      }
      // Reset scroll state for the next step
      this.isScrolledToEnd = false;
      // Scroll to top of dialog content
      this.scrollToTop();
    }

    
  }

  previousStep(): void {
    let previousPageName = '';

    if(this.currentStep == 1){
      EclaimsAdobeAnalytics.trackLinkClick({
        label: EclaimsAdobeTags.GUIDANCE.PRIVACY_POLICY_RETURN_LINK_NAME
      });
      previousPageName = EclaimsAdobeTags.GUIDANCE.CONDITION_TERMS_PAGE_NAME;
    }else if(this.currentStep == 2){
      EclaimsAdobeAnalytics.trackLinkClick({
        label: EclaimsAdobeTags.GUIDANCE.USAGE_COND_RETURN_LINK_NAME
      });
      previousPageName = EclaimsAdobeTags.GUIDANCE.PRIVACY_POLICY_PAGE_NAME;
    }

    if (this.currentStep > 0) {
      this.currentStep--;
      if (previousPageName) {
        EclaimsAdobeAnalytics.trackPageLoad(previousPageName);
      }
      // Reset scroll state when going back
      this.isScrolledToEnd = false;
      // Scroll to top of dialog content
      this.scrollToTop();
    }
  }

  private scrollToTop(): void {
    // Use setTimeout to ensure DOM is updated after step change
    setTimeout(() => {
      if (this.dialogContent && this.dialogContent.nativeElement) {
        this.dialogContent.nativeElement.scrollTop = 0;
      }
    }, 0);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 5; // 5px tolerance for scroll detection

    // Check if scrolled to bottom
    const isAtBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - threshold;

    this.isScrolledToEnd = isAtBottom;
  }

  onAccept(): void {
    EclaimsAdobeAnalytics.trackLinkClick({
      label: EclaimsAdobeTags.GUIDANCE.USAGE_COND_AGREED_LINK_NAME,
      url: buildSafeHashRouteUrl('/user-identification')
    });
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
    EclaimsAdobeAnalytics.trackLinkClick({
      label: EclaimsAdobeTags.GUIDANCE.CONDITION_CLOSE_LINK_NAME
    })
  }
}
